'use client';

import { useCallback, useState } from 'react';

import { buildMailtoFallback, describeLeadError, submitLead, type Lead } from '@/services/leadsApi';

export type SubmissionStatus = 'idle' | 'submitting' | 'success' | 'error';

export interface LeadSubmission {
  status: SubmissionStatus;
  errorMessage: string | null;
  /** Prefilled mailto link, available once a submission has failed. */
  fallbackMailto: string | null;
  submit: (lead: Lead) => Promise<boolean>;
  reset: () => void;
}

/**
 * Shared submit state for every lead form on the site: one status machine, one
 * error message, and an email fallback so a failed POST never loses the lead.
 */
export function useLeadSubmission(): LeadSubmission {
  const [status, setStatus] = useState<SubmissionStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fallbackMailto, setFallbackMailto] = useState<string | null>(null);

  const submit = useCallback(async (lead: Lead) => {
    setStatus('submitting');
    setErrorMessage(null);
    setFallbackMailto(null);

    try {
      await submitLead(lead);
      setStatus('success');
      return true;
    } catch (error) {
      setErrorMessage(describeLeadError(error));
      setFallbackMailto(buildMailtoFallback(lead));
      setStatus('error');
      return false;
    }
  }, []);

  const reset = useCallback(() => {
    setStatus('idle');
    setErrorMessage(null);
    setFallbackMailto(null);
  }, []);

  return { status, errorMessage, fallbackMailto, submit, reset };
}
